import React from 'react';
import './ClassSystemGauge.css';

/**
 * ClassSystemGauge — Renders class-specific combat system UI.
 * Fighter: Momentum bar (0-10)
 * Thief: Shadow Marks counter
 * Mage: Spell Slot rack + Release button
 * Cleric: Aspect indicator + switch buttons
 * Psion: Focus bar (0-10) with threshold markers
 * Druid: Bond bar (0-10) or Wild Shape form indicator
 */
const ClassSystemGauge = ({ classSystemState, onAction, isPlayerTurn }) => {
  if (!classSystemState || !classSystemState.type) return null;

  const { type } = classSystemState;

  // ── FIGHTER: Momentum ──
  if (type === 'momentum') {
    const { momentum = 0, maxMomentum = 10, attackedThisTurn } = classSystemState;
    const pct = (momentum / maxMomentum) * 100;
    const tier = momentum >= 8 ? 'high' : momentum >= 4 ? 'mid' : 'low';
    return (
      <div className="class-gauge class-gauge-momentum">
        <div className="gauge-label">
          <span className="gauge-icon">⚡</span>
          <span className="gauge-name">Momentum</span>
          <span className="gauge-value">{momentum}/{maxMomentum}</span>
        </div>
        <div className="gauge-bar-track">
          <div className={`gauge-bar-fill momentum-${tier}`} style={{ width: `${pct}%` }} />
          {/* Threshold markers */}
          <div className="gauge-marker" style={{ left: '30%' }} title="3: Block bonus" />
          <div className="gauge-marker" style={{ left: '80%' }} title="8: Max finisher" />
        </div>
        {!attackedThisTurn && momentum > 0 && (
          <div className="gauge-hint">⚠️ Decay -3 if no attack this turn</div>
        )}
      </div>
    );
  }

  // ── THIEF: Shadow Marks ──
  if (type === 'marks') {
    const { marks = 0, preparation } = classSystemState;
    return (
      <div className="class-gauge class-gauge-marks">
        <div className="gauge-label">
          <span className="gauge-icon">🎯</span>
          <span className="gauge-name">Shadow Marks</span>
          <span className="gauge-value">{marks}</span>
        </div>
        <div className="marks-display">
          {Array.from({ length: Math.min(marks, 10) }).map((_, i) => (
            <span key={i} className={`mark-pip${i >= 5 ? ' mark-pip-stun' : i >= 3 ? ' mark-pip-debuff' : ''}`}>◆</span>
          ))}
          {marks > 10 && <span className="marks-overflow">+{marks - 10}</span>}
        </div>
        <div className="marks-thresholds">
          {marks >= 1 && <span className="threshold-badge threshold-active">1+: 8dmg/mark</span>}
          {marks >= 3 && <span className="threshold-badge threshold-active">3+: ATT debuff</span>}
          {marks >= 5 && <span className="threshold-badge threshold-active">5+: STUN</span>}
        </div>
        {preparation && <div className="gauge-hint">🗡️ Preparation: Next attack +25%!</div>}
      </div>
    );
  }

  // ── MAGE: Spell Slots ──
  if (type === 'spellSlots') {
    const {
      slots = [], slotCount = 3, overchargeReady,
      afterglow, passiveBuffs = {}, isWizard,
      slotRoles = ['Primer', 'Amplifier', 'Finisher'],
    } = classSystemState;
    const maxSlots = slotCount;
    const filledSlots = slots.filter(s => s !== null);
    const isFull = filledSlots.length === maxSlots;

    // Active passive buffs for display
    const activePassives = [];
    if (passiveBuffs.damage)          activePassives.push({ emoji: '🔥', label: `+${Math.round(passiveBuffs.damage * 100)}% dmg`, type: 'fire' });
    if (passiveBuffs.evasion)         activePassives.push({ emoji: '❄️', label: `+${passiveBuffs.evasion}% dodge`, type: 'ice' });
    if (passiveBuffs.draw)            activePassives.push({ emoji: '⚡', label: `+${passiveBuffs.draw} draw`, type: 'lightning' });
    if (passiveBuffs.spellAmp)        activePassives.push({ emoji: '💜', label: `+${Math.round(passiveBuffs.spellAmp * 100)}% spell`, type: 'arcane' });
    if (passiveBuffs.hpRegen)         activePassives.push({ emoji: '🌟', label: `+${passiveBuffs.hpRegen} HP/turn`, type: 'holy' });
    if (passiveBuffs.lifesteal)       activePassives.push({ emoji: '🌑', label: `+${Math.round(passiveBuffs.lifesteal * 100)}% leech`, type: 'dark' });
    if (passiveBuffs.poisonDot)       activePassives.push({ emoji: '🌿', label: `+${passiveBuffs.poisonDot} psn/turn`, type: 'nature' });
    if (passiveBuffs.dr)              activePassives.push({ emoji: '🛡️', label: `+${passiveBuffs.dr} DR`, type: 'force' });
    if (passiveBuffs.monsterDrReduce) activePassives.push({ emoji: '🧪', label: `-${passiveBuffs.monsterDrReduce} foe DR`, type: 'acid' });
    if (passiveBuffs.ap)              activePassives.push({ emoji: '💨', label: `+${passiveBuffs.ap} AP/turn`, type: 'air' });
    if (passiveBuffs.block)           activePassives.push({ emoji: '🪨', label: `+${passiveBuffs.block} block/turn`, type: 'earth' });
    if (passiveBuffs.mpRegen)         activePassives.push({ emoji: '💧', label: `+${passiveBuffs.mpRegen} MP/turn`, type: 'water' });
    if (passiveBuffs.slow)            activePassives.push({ emoji: '❄️', label: `+${passiveBuffs.slow}% slow`, type: 'cold' });
    if (passiveBuffs.stun)            activePassives.push({ emoji: '⚡', label: `+${passiveBuffs.stun}% stun`, type: 'electricity' });

    return (
      <div className="class-gauge class-gauge-spellslots">
        <div className="gauge-label">
          <span className="gauge-icon">🔮</span>
          <span className="gauge-name">Spell Slots{isWizard ? ' (Wizard)' : ''}</span>
          <span className="gauge-value">{filledSlots.length}/{maxSlots}</span>
        </div>

        {/* Power gauge bar */}
        <div className="spell-power-track">
          <div
            className={`spell-power-fill${isFull ? ' spell-power-full' : ''}`}
            style={{ width: `${(filledSlots.length / maxSlots) * 100}%` }}
          />
        </div>

        {/* Slot rack with position labels */}
        <div className="spell-slot-rack">
          {Array.from({ length: maxSlots }).map((_, i) => {
            const slot = slots[i];
            const role = slotRoles[i] || `Slot ${i + 1}`;
            const elemClass = slot ? `spell-slot-${(slot.element || 'default').toLowerCase()}` : '';
            const mergedClass = slot && slot.merged ? ' spell-slot-merged' : '';
            return (
              <div key={i} className={`spell-slot${slot ? ' spell-slot-filled' : ' spell-slot-empty'} ${elemClass}${mergedClass}`}>
                <div className="slot-role-label">{role}</div>
                {slot ? (
                  <div className="spell-slot-content" title={`${slot.cardName} (${slot.element || '?'})`}>
                    <span className="slot-element">{getElementEmoji(slot.element)}</span>
                    <span className="slot-name">{slot.cardName}</span>
                    {slot.merged && <span className="slot-merged-badge" title="Wizard merge">⊕</span>}
                  </div>
                ) : (
                  <span className="slot-empty-label">Empty</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Passive buff badges */}
        {activePassives.length > 0 && (
          <div className="spell-passive-buffs">
            {activePassives.map((p, i) => (
              <span key={i} className={`passive-badge passive-badge-${p.type}`}>
                {p.emoji} {p.label}
              </span>
            ))}
          </div>
        )}

        {/* Release / Unload buttons */}
        {filledSlots.length > 0 && isPlayerTurn && (
          <div className="spell-action-row">
            <button
              className={`class-action-btn release-spells-btn${isFull ? ' release-spells-btn-full' : ''}`}
              onClick={() => onAction({ type: 'release_spells' })}
            >
              🔮 Release ({filledSlots.length} spell{filledSlots.length > 1 ? 's' : ''})
              {filledSlots.length >= 2 && ` ×${getComboMultiplier(filledSlots.length, overchargeReady, isWizard)}`}
            </button>
            <button
              className="class-action-btn unload-spells-btn"
              onClick={() => onAction({ type: 'unload_spells' })}
              title="Return loaded spells to your hand"
            >
              ↩ Unload
            </button>
          </div>
        )}

        {/* Status indicators */}
        {overchargeReady && <div className="gauge-hint">⚡ Overcharged: Next release ×1.5 bonus!</div>}
        {afterglow && <div className="gauge-hint spell-afterglow-hint">✨ Spell Afterglow: Next spell costs 0 MP!</div>}
      </div>
    );
  }

  // ── CLERIC: Divine Aspects ──
  if (type === 'aspects') {
    const { aspect = 'grace', lastSwitch = 0, sacrificeHpLost = 0 } = classSystemState;
    const aspects = {
      grace:     { name: 'Grace',     emoji: '💚', color: '#27ae60', desc: '+50% heal, -30% dmg, +3 HP/turn' },
      judgment:  { name: 'Judgment',  emoji: '⚔️', color: '#e74c3c', desc: '+50% dmg, heals→damage, -30% block' },
      sacrifice: { name: 'Sacrifice', emoji: '🩸', color: '#8e44ad', desc: '×2 all effects, 30% self-harm' },
    };
    const current = aspects[aspect] || aspects.grace;
    return (
      <div className="class-gauge class-gauge-aspects">
        <div className="gauge-label">
          <span className="gauge-icon">{current.emoji}</span>
          <span className="gauge-name">Aspect: {current.name}</span>
        </div>
        <div className="aspect-description" style={{ color: current.color }}>{current.desc}</div>
        <div className="aspect-buttons">
          {Object.entries(aspects).map(([key, info]) => (
            <button
              key={key}
              className={`aspect-btn${aspect === key ? ' aspect-btn-active' : ''}`}
              style={{ borderColor: info.color, color: aspect === key ? '#fff' : info.color, backgroundColor: aspect === key ? info.color : 'transparent' }}
              onClick={() => onAction({ type: 'switch_aspect', aspect: key })}
              disabled={!isPlayerTurn || aspect === key}
              title={info.desc}
            >
              {info.emoji} {info.name}
            </button>
          ))}
        </div>
        {aspect === 'sacrifice' && sacrificeHpLost > 0 && (
          <div className="gauge-hint">🩸 Sacrifice accumulated: {sacrificeHpLost} HP damage stored</div>
        )}
      </div>
    );
  }

  // ── PSION: Focus ──
  if (type === 'focus') {
    const { focus = 0, maxFocus = 10, canEcho, isOmniscient } = classSystemState;
    const pct = (focus / maxFocus) * 100;
    const tier = focus >= 9 ? 'omni' : focus >= 7 ? 'echo' : focus >= 4 ? 'mid' : 'low';
    return (
      <div className="class-gauge class-gauge-focus">
        <div className="gauge-label">
          <span className="gauge-icon">🧠</span>
          <span className="gauge-name">Focus</span>
          <span className="gauge-value">{focus}/{maxFocus}</span>
        </div>
        <div className="gauge-bar-track">
          <div className={`gauge-bar-fill focus-${tier}`} style={{ width: `${pct}%` }} />
          <div className="gauge-marker" style={{ left: '70%' }} title="7: Echo unlocked" />
          <div className="gauge-marker gauge-marker-major" style={{ left: '90%' }} title="9: Omniscience" />
        </div>
        <div className="focus-thresholds">
          {canEcho && <span className="threshold-badge threshold-active">🧠 Echo (7+)</span>}
          {isOmniscient && <span className="threshold-badge threshold-active">👁️ Omniscience (9+)</span>}
        </div>
      </div>
    );
  }

  // ── DRUID: Bond ──
  if (type === 'bond') {
    const { bond = 0, maxBond = 10, bondThreshold = 0 } = classSystemState;
    const pct = (bond / maxBond) * 100;
    const tier = bond >= 9 ? 'high' : bond >= 6 ? 'mid' : bond >= 3 ? 'low' : 'none';
    return (
      <div className="class-gauge class-gauge-bond">
        <div className="gauge-label">
          <span className="gauge-icon">🐾</span>
          <span className="gauge-name">Bond</span>
          <span className="gauge-value">{bond}/{maxBond}</span>
        </div>
        <div className="gauge-bar-track">
          <div className={`gauge-bar-fill bond-${tier}`} style={{ width: `${pct}%` }} />
          <div className="gauge-marker" style={{ left: '30%' }} title="3: +25% pet damage" />
          <div className="gauge-marker" style={{ left: '60%' }} title="6: Pet reflects" />
          <div className="gauge-marker gauge-marker-major" style={{ left: '90%' }} title="9: Pet double strike" />
        </div>
        <div className="bond-thresholds">
          {bond >= 3 && <span className="threshold-badge threshold-active">3+: +25% pet dmg</span>}
          {bond >= 6 && <span className="threshold-badge threshold-active">6+: Reflect</span>}
          {bond >= 9 && <span className="threshold-badge threshold-active">9+: Double Strike</span>}
        </div>
        {bondThreshold > 0 && (
          <div className="gauge-hint">🐾 Feral Synergy active at threshold {bondThreshold}!</div>
        )}
      </div>
    );
  }

  // ── DRUID: Wild Shape ──
  if (type === 'wildShape') {
    const { wildShapeActive, wildShapeRounds, currentForm, availableForms = [] } = classSystemState;
    if (wildShapeActive && currentForm) {
      const formInfo = FORM_INFO[currentForm] || { name: currentForm, emoji: '🐾' };
      return (
        <div className="class-gauge class-gauge-wildshape">
          <div className="gauge-label">
            <span className="gauge-icon">{formInfo.emoji}</span>
            <span className="gauge-name">Wild Shape: {formInfo.name}</span>
          </div>
          <div className="wildshape-timer">
            {Array.from({ length: wildShapeRounds || 0 }).map((_, i) => (
              <span key={i} className="turn-pip turn-pip-active">●</span>
            ))}
            {Array.from({ length: Math.max(0, 2 - (wildShapeRounds || 0)) }).map((_, i) => (
              <span key={`e${i}`} className="turn-pip turn-pip-empty">○</span>
            ))}
            <span className="timer-label">{wildShapeRounds} turn{wildShapeRounds !== 1 ? 's' : ''} left</span>
          </div>
        </div>
      );
    }
    // Not transformed — show available forms if Wild Shape card exists
    return (
      <div className="class-gauge class-gauge-wildshape">
        <div className="gauge-label">
          <span className="gauge-icon">🐾</span>
          <span className="gauge-name">Wild Shape</span>
          <span className="gauge-hint-inline">Play Wild Shape card to transform</span>
        </div>
      </div>
    );
  }

  return null;
};

// Helper: element emoji for spell slots
function getElementEmoji(element) {
  const map = {
    Fire: '🔥', Ice: '❄️', Lightning: '⚡', Arcane: '✨',
    Holy: '✝️', Nature: '🌿', Psionic: '🧠', Dark: '🌑',
  };
  return map[element] || '🔮';
}

// Helper: combo multiplier text for release button
function getComboMultiplier(count, overcharged, isWizard) {
  let base;
  if (isWizard) {
    base = count === 1 ? 1.0 : count === 2 ? 1.5 : 2.0;
  } else {
    base = count === 1 ? 1.0 : count === 2 ? 1.3 : count === 3 ? 1.6 : 1.8;
  }
  return overcharged ? (base * 1.5).toFixed(1) : base.toFixed(1);
}

// Wild shape form display info
const FORM_INFO = {
  bear:    { name: 'Bear',    emoji: '🐻' },
  wolf:    { name: 'Wolf',    emoji: '🐺' },
  hawk:    { name: 'Hawk',    emoji: '🦅' },
  serpent: { name: 'Serpent', emoji: '🐍' },
};

export default ClassSystemGauge;
